import { v4 as uuid } from 'uuid';
import { compact, map } from 'lodash';
import { toUUID } from '../../util/transform';
import { epoch, utc, formatEpochAsDate } from '../../util/datetime';
import type { IInsurance } from './Insurance.interface';
import { InsuranceType } from './Insurance.enum';
import { PaymentFrequency } from '../global/PaymentFrequency.enum';

export default class Insurance implements IInsurance {
  id: string;
  idReference: string;
  insuranceType: InsuranceType;
  name: string;
  policyNumber: string;
  value: number;
  premium: number;
  premiumFrequency: PaymentFrequency;
  healthCoverType: string;
  renewalDate: number;
  idAsset: string;
  idContacts: Array<string>;
  timeStamp: number;
  isActive: boolean;

  getName() {
    return this.name;
  }

  getInsuranceTypeName() {
    return this.insuranceType.title;
  }

  isInsuranceHomeContents() {
    return this.insuranceType.id === Insurance.getAvailableInsuranceTypes()[0].id;
  }

  isInsuranceVehicle() {
    return this.insuranceType.id === Insurance.getAvailableInsuranceTypes()[1].id;
  }

  isInsurancePrivateHealth() {
    return this.insuranceType.id === Insurance.getAvailableInsuranceTypes()[2].id;
  }

  isInsuranceTpdProvider() {
    return this.insuranceType.id === Insurance.getAvailableInsuranceTypes()[3].id;
  }

  isInsuranceLifeProvider() {
    return this.insuranceType.id === Insurance.getAvailableInsuranceTypes()[4].id;
  }

  isInsuranceIncomeProtection() {
    return this.insuranceType.id === Insurance.getAvailableInsuranceTypes()[5].id;
  }

  isInsuranceTrauma() {
    return this.insuranceType.id === Insurance.getAvailableInsuranceTypes()[6].id;
  }

  isInsuranceLoanProtection() {
    return this.insuranceType.id === Insurance.getAvailableInsuranceTypes()[7].id;
  }

  isInsuranceGap() {
    return this.insuranceType.id === Insurance.getAvailableInsuranceTypes()[8].id;
  }

  isInsuranceMotorWarrantyProducts() {
    return this.insuranceType.id === Insurance.getAvailableInsuranceTypes()[9].id;
  }

  isInsuranceOther() {
    return this.insuranceType.id === Insurance.getAvailableInsuranceTypes()[10].id;
  }

  getRenewalDate(timeZone, locale, localeFormat) {
    return formatEpochAsDate(this.renewalDate, timeZone, locale, localeFormat);
  }

  getRenewalDateSort() {
    return this.renewalDate;
  }

  static getAvailableInsuranceTypes = () => InsuranceType.available();

  static build = (data: IInsurance) => new Insurance(data);
  static blank = (insuranceType: InsuranceType) =>
    new Insurance({ id: uuid(), insuranceType, isActive: true });

  /**
   * Constructor
   * @param {IInsurance} data
   */
  constructor(data?: IInsurance) {
    this.id = toUUID(data?.id) || uuid();
    // TODO: idTicketLiability is legacy structure
    this.idReference = toUUID(data?.idReference || data?.idTicketInsurance);
    this.insuranceType = InsuranceType.of(data?.insuranceType?.id || data?.insuranceType?.name);
    this.name = data?.name || '';
    this.policyNumber = data?.policyNumber || '';
    this.value = parseFloat(data?.value || 0);
    this.premium = parseFloat(data?.premium || 0);
    this.premiumFrequency = PaymentFrequency.of(
      data?.premiumFrequency?.name || `f${data?.premiumFrequency}`
    );
    this.healthCoverType = data?.healthCoverType || '';
    this.renewalDate = data?.renewalDate ? epoch(utc(data?.renewalDate)) : 0;
    this.idAsset = toUUID(data?.idAsset);
    this.idContacts = map(compact(data?.idContacts), (obj) => toUUID(obj));
    this.timeStamp = epoch(utc(data?.timeStamp));
    this.isActive = data?.isActive === true;
  }
}
